import React, { useEffect, useState } from 'react';
import useAolSaveHandler from '../../../../app/useAolSaveHandler';
import { EngineDataTypeEnum, NoonReportSectionEnum } from '../../../../types/ViewModel/AolEnums';
import { AolDataItem } from '../../../../types/ViewModel/AolItem';
import { getConsumptionCost, hasInvalidFields, hasMissingValues } from '../../../../utils/aolFieldsHelper';
import { ErrorText } from '../../../shared/error-text/ErrorText.style';
import OdfjellInputComponent, { InputChangeData } from '../../../common/odfjell-input-component/OdfjellInputComponent';
import { EnginesDataVM } from '../EnginesSection';
import { EngineListWrapper } from '../EnginesSection.style';
import { SectionInnerWrapper, SubSectionHeader } from '../../aol-container/AolContainer.style';
import useAolItemsHelper from '../../../../utils/aol/useAolItemsHelper';
import ConsumptionContainer from '../../consumption/ConsumptionContainer';
import { Consumption } from 'src/types/ViewModel/Aol/Consumption';
import { FuelType } from 'src/types/ViewModel/Bunkering/FuelType';

type Props = {
    enginesDataVM: EnginesDataVM;
    onChange: () => void;
    aolDataItem: AolDataItem;
    hasEmptyValues;
    isReadOnly: boolean;
    addConsumptionRow: (consumption: Consumption[]) => void;
};

const IncineratorSectionComponent: React.FC<Props> = ({ enginesDataVM, onChange, aolDataItem, hasEmptyValues, isReadOnly, addConsumptionRow }) => {
    const { saveAolEvent } = useAolSaveHandler();
    const [hasEmptyValuesLocal, setHasEmptyValuesLocal] = useState(false);
    const { getVoyageFuelPrice } = useAolItemsHelper();

    useEffect(() => {
        if (
            saveAolEvent &&
            saveAolEvent.type === 'save_section' &&
            saveAolEvent.section === NoonReportSectionEnum.Engines &&
            aolDataItem &&
            saveAolEvent.aolDataItem.id === aolDataItem.id
        ) {
            checkOnSave();
        }
    }, [saveAolEvent]);

    useEffect(() => {
        setHasEmptyValuesLocal(false);
        hasEmptyValues.current = false;
    }, [aolDataItem]);

    const onInputChange = (changeObject: InputChangeData) => {
        const name = changeObject.event.target.name;
        const value = changeObject.event.target.value;
        changeObject.dataObject[name] = value ? +value : undefined;

        if (name === 'incineratorConsumptionMgo') {
            changeObject.dataObject.incineratorConsumptionMgoCost = getConsumptionCost(FuelType.LGO, getVoyageFuelPrice(aolDataItem), +value);
        }

        resetHasEmptyValues();
        hasInvalidFields(enginesDataVM, name, changeObject.isValid);
        onChange();
    };

    const resetHasEmptyValues = () => {
        if (hasEmptyValuesLocal) {
            setHasEmptyValuesLocal(false);
            hasEmptyValues.current = false;
        }
    };

    const checkOnSave = () => {
        const ignoreList: string[] = [];
        const data = {
            incineratorConsumptionMgo: enginesDataVM.incineratorConsumptionMgo,
        };
        const hasMissing = hasMissingValues(data, ignoreList);

        if (hasMissing) {
            setHasEmptyValuesLocal(true);
            hasEmptyValues.current = true;
        }
    };

    return (
        <>
            <SectionInnerWrapper>
                <SubSectionHeader>Incinerator</SubSectionHeader>
                <EngineListWrapper>
                    {aolDataItem.aolVersion >= 5 ? (
                        <ConsumptionContainer
                            aolItem={aolDataItem}
                            engineType={EngineDataTypeEnum.Incinerator}
                            consumption={enginesDataVM?.consumption}
                            isReadOnly={isReadOnly}
                            onChange={onInputChange}
                            addConsumptionRow={addConsumptionRow}
                        />
                    ) : (
                        <OdfjellInputComponent
                            isReadOnly={isReadOnly}
                            type={'number'}
                            description={'MGO'}
                            cost={enginesDataVM.incineratorConsumptionMgoCost}
                            dataObject={enginesDataVM}
                            name={'incineratorConsumptionMgo'}
                            min={0}
                            max={50}
                            value={enginesDataVM.incineratorConsumptionMgo}
                            onChange={onInputChange}
                            unit={'MT'}
                        />
                    )}
                </EngineListWrapper>
            </SectionInnerWrapper>
            {hasEmptyValuesLocal && <ErrorText>You must enter incinerator data before saving.</ErrorText>}
        </>
    );
};

export default IncineratorSectionComponent;
